import React from 'react';
import { DocumentItem } from '../../../types/appModels';
import DocumentItemComponent from '../DocumentItem/DocumentItem';
import { formatCustomerName } from '../../../utility/formatCustomerName';
import { decodeSlashes } from '../../../utility/formatCustomerName';
import css from './DocumentList.module.css';

interface DocumentListProps {
  items: DocumentItem[];
  currentPath: string;
  selectedFiles: Set<string>;
  onItemClick: (item: DocumentItem) => void;
  onFileDownload: (path: string) => void;
}

const DocumentList: React.FC<DocumentListProps> = ({
  items,
  currentPath,
  selectedFiles,
  onItemClick,
  onFileDownload
}) => {
  
  const formatItemName = (name: string): string => {
    // First decode any encoded slashes and URL encoding
    const decodedName = decodeSlashes(decodeURIComponent(name));
    
    // Only format customer names at the root level (when currentPath is empty)
    if (!currentPath) {
      return formatCustomerName(decodedName);
    }
    return decodedName;
  };
  
  // Sort items numerically if they are all numbers
  const sortedItems = React.useMemo(() => {
    // Create a copy to sort
    const itemsToSort = [...items];
    
    // Check if all item names are integers (or can be parsed as integers)
    const allAreIntegers = itemsToSort.every(item => {
      // Decode any encoded slashes first
      const decodedName = decodeSlashes(decodeURIComponent(item.name));
      // Remove any file extension
      const nameWithoutExtension = decodedName.split('.')[0];
      // Check if it's a valid integer
      return /^\d+$/.test(nameWithoutExtension.trim());
    });
    
    if (allAreIntegers) {
      // Sort numerically
      return itemsToSort.sort((a, b) => {
        const aName = parseInt(decodeSlashes(decodeURIComponent(a.name)).split('.')[0].trim(), 10);
        const bName = parseInt(decodeSlashes(decodeURIComponent(b.name)).split('.')[0].trim(), 10);
        return aName - bName;
      });
    }
    
    // If they're not all integers, sort alphabetically
    return itemsToSort.sort((a, b) => {
      const aName = formatItemName(a.name);
      const bName = formatItemName(b.name);
      return aName.localeCompare(bName);
    });
  }, [items, currentPath]);

  return (
    <div className={css.content}>
      {sortedItems.map((item) => (
        <DocumentItemComponent
          key={item.path}
          item={item}
          formattedName={formatItemName(item.name)}
          isSelected={item.type === 'File' && selectedFiles.has(item.path)}
          onItemClick={() => onItemClick(item)}
          onFileDownload={onFileDownload}
        />
      ))}
    </div>
  );
};

export default DocumentList;